import React, { useState, useRef, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  User,
  AlertTriangle,
  CheckCircle2,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Trash2,
} from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../App";
import { usePermissions } from "../HasPermission";
import LoadingSpinner from "../ui/LoadingSpinner";

const StudentCard = ({
  student,
  colors,
  isDark,
  t,
  user,
  onUpdateStatus,
  onNavigateToStudent,
  onAddMeasurement,
  onRemoveStudent,
  canRemoveStudent,
  hasPermission,
  getStudentInitials,
  getStatusConfig,
  statusOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const statusConfig = getStatusConfig(student.currentStatus);
  const StatusIcon = statusConfig.icon;
  const isStatusScheduled =
    (student.currentStatus || "scheduled") === "scheduled";
  const isDisabledForTrainer = user?.role === "trainer" && !isStatusScheduled;

  const getLastMeasurementInfo = (measurements) => {
    if (!measurements || measurements.length === 0) {
      return {
        hasRecent: false,
        message: t("sessionDetails.measurement.notFound"),
      };
    }

    const lastMeasurement = measurements.sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    )[0];
    const daysSince = moment().diff(moment(lastMeasurement.date), "days");
    const monthsSince = moment().diff(
      moment(lastMeasurement.date),
      "months",
      true,
    );

    if (monthsSince >= 0.1) {
      return {
        hasRecent: false,
        message: t("sessionDetails.measurement.monthsSince", {
          count: Math.round(monthsSince * 10) / 10,
        }),
      };
    }

    return {
      hasRecent: true,
      message: t("sessionDetails.measurement.daysSince", { count: daysSince }),
    };
  };

  const measurementInfo = getLastMeasurementInfo(student.measurements);

  return (
    <div className="pb-6 last:pb-0 border-b last:border-0 border-white/5">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4">
        <div className="flex flex-1 items-center gap-4 min-w-0">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/20 shrink-0">
            <span className="text-sm font-bold text-white">
              {getStudentInitials(student.name, student.surname)}
            </span>
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <span
                className={`font-bold ${colors.textPrimary} text-base leading-tight truncate`}
              >
                {student.name} {student.surname}
              </span>
              <button
                onClick={() => onNavigateToStudent(student.id)}
                className="inline-flex items-center space-x-1.5 px-2 py-1 rounded-md text-[10px] uppercase font-bold tracking-wider bg-purple-500/10 text-purple-500 hover:bg-purple-500 hover:text-white transition-all duration-200 shrink-0"
              >
                <Eye className="w-3 h-3" />
                <span>{t("sessionDetails.viewProfile")}</span>
              </button>
            </div>
            <div className={`text-xs ${colors.textTertiary} truncate`}>
              {student.email}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <div className="relative min-w-[130px]" ref={dropdownRef}>
            <button
              disabled={isDisabledForTrainer}
              onClick={() => !isDisabledForTrainer && setIsOpen(!isOpen)}
              className={`w-full h-9 flex items-center justify-between px-3 rounded-xl transition-all duration-300 ${colors.inputBg} ${colors.inputBorder} border outline-none ${isDisabledForTrainer ? "opacity-60 cursor-not-allowed" : "hover:border-purple-500/30"} ${isOpen ? "ring-2 ring-purple-500/50" : ""}`}
            >
              <div className="flex items-center gap-2">
                <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
                <span
                  className={`text-xs font-bold ${colors.textPrimary} leading-none truncate`}
                >
                  {statusConfig.label}
                </span>
              </div>
              <ChevronDown
                className={`w-3 h-3 ${colors.textTertiary} transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isOpen && (
              <div
                className={`absolute top-full right-0 mt-2 w-48 p-1.5 rounded-xl z-[100] ${isDark ? "bg-zinc-800" : "bg-white"} border ${colors.tableBorder} shadow-2xl overflow-hidden`}
              >
                <div className="flex flex-col gap-1">
                  {statusOptions.map((option) => {
                    const st = student.currentStatus || "scheduled";
                    const isActive =
                      st === option.value ||
                      (st === "no_show" && option.value === "no-show");
                    return (
                      <button
                        key={option.value}
                        onClick={() => {
                          onUpdateStatus(
                            student.sessionId,
                            student.organizationId,
                            option.value,
                          );
                          setIsOpen(false);
                        }}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all duration-200 text-left ${isActive ? "bg-gray-100 dark:bg-white/10" : "hover:bg-gray-50 dark:hover:bg-white/5"}`}
                      >
                        <option.icon
                          className={`w-3.5 h-3.5 ${option.colorClass}`}
                        />
                        <span
                          className={`text-xs font-medium ${isActive ? colors.textPrimary : colors.textSecondary}`}
                        >
                          {option.label}
                        </span>
                        {isActive && (
                          <CheckCircle
                            className={`w-3 h-3 ml-auto ${option.colorClass}`}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div
            className={`group relative flex items-center gap-2 px-3 py-2 ${colors.inputBg} rounded-lg border ${colors.inputBorder} cursor-help`}
          >
            <div className="w-2 h-2 bg-green-500 rounded-full shadow-[0_0_8px_rgba(34,197,94,0.5)] shrink-0"></div>
            <div className="flex items-baseline gap-1.5">
              <span
                className={`text-[9px] uppercase font-bold tracking-wider ${colors.textTertiary}`}
              >
                {t("sessionDetails.used", "Kullanılan")}:
              </span>
              <span className={`${colors.textPrimary} font-bold text-xs`}>
                {student.currentSubscriptionData?.usedSessions || 0} /{" "}
                {student.currentSubscriptionData?.totalSessions || 0}
              </span>
            </div>

            {/* Detailed Stats Tooltip */}
            <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-3 py-2.5 rounded-xl text-xs font-medium shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[110] ${isDark ? "bg-zinc-800 text-white border border-white/10 shadow-black/50" : "bg-white text-slate-800 border border-slate-200 shadow-slate-200/50"}`}>
              <div className="flex flex-col gap-1.5 whitespace-nowrap">
                <div className="flex justify-between gap-6 items-center">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    <span className={isDark ? "text-slate-300" : "text-slate-600"}>{t("sessionDetails.studentStats.scheduled")}:</span>
                  </div>
                  <span className="font-bold text-[13px]">{student.currentSubscriptionData?.scheduledSessions || 0}</span>
                </div>
                <div className="flex justify-between gap-6 items-center">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                    <span className={isDark ? "text-slate-300" : "text-slate-600"}>{t("sessionDetails.studentStats.completed")}:</span>
                  </div>
                  <span className="font-bold text-[13px]">{student.currentSubscriptionData?.completedSessions || 0}</span>
                </div>
                <div className="flex justify-between gap-6 items-center pt-1 border-t border-slate-500/20">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                    <span className={isDark ? "text-slate-300" : "text-slate-600"}>{t("sessionDetails.studentStats.cancelled")}:</span>
                  </div>
                  <span className="font-bold text-[13px]">{student.currentSubscriptionData?.cancelledSessions || 0}</span>
                </div>
              </div>
              <div className={`absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent ${isDark ? "border-t-zinc-800" : "border-t-white"}`}></div>
            </div>
          </div>

          <div
            className={`group relative flex items-center gap-2 px-3 py-2 ${colors.inputBg} rounded-lg border ${colors.inputBorder} cursor-help`}
          >
            <div className="w-2 h-2 bg-rose-500 rounded-full shadow-[0_0_8px_rgba(244,63,94,0.5)] shrink-0"></div>
            <div className="flex items-baseline gap-1.5">
              <span
                className={`text-[9px] uppercase font-bold tracking-wider ${colors.textTertiary}`}
              >
                {t("sessionDetails.studentStats.cancelledLabel")}:
              </span>
              <span className={`${colors.textPrimary} font-bold text-xs`}>
                {student.currentSubscriptionData?.freeCancellationsUsed || 0} /{" "}
                {student.currentSubscriptionData?.allowedFreeCancellations || 0}
              </span>
            </div>

            {/* Breakdown tooltip: total cancellations vs free (on-time) used vs credit-deducting */}
            <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-3 py-2.5 rounded-xl text-xs font-medium shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[110] ${isDark ? "bg-zinc-800 text-white border border-white/10 shadow-black/50" : "bg-white text-slate-800 border border-slate-200 shadow-slate-200/50"}`}>
              <div className="flex flex-col gap-1.5 whitespace-nowrap">
                <div className="flex justify-between gap-6 items-center">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                    <span className={isDark ? "text-slate-300" : "text-slate-600"}>{t("sessionDetails.studentStats.totalCancelled")}:</span>
                  </div>
                  <span className="font-bold text-[13px]">{student.currentSubscriptionData?.cancelledSessions || 0}</span>
                </div>
                <div className="flex justify-between gap-6 items-center">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                    <span className={isDark ? "text-slate-300" : "text-slate-600"}>{t("sessionDetails.studentStats.freeUsed")}:</span>
                  </div>
                  <span className="font-bold text-[13px]">{student.currentSubscriptionData?.freeCancellationsUsed || 0} / {student.currentSubscriptionData?.allowedFreeCancellations || 0}</span>
                </div>
                <div className="flex justify-between gap-6 items-center pt-1 border-t border-slate-500/20">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                    <span className={isDark ? "text-slate-300" : "text-slate-600"}>{t("sessionDetails.studentStats.creditDeducted")}:</span>
                  </div>
                  <span className="font-bold text-[13px]">{Math.max(0, (student.currentSubscriptionData?.cancelledSessions || 0) - (student.currentSubscriptionData?.freeCancellationsUsed || 0))}</span>
                </div>
              </div>
              <div className={`absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent ${isDark ? "border-t-zinc-800" : "border-t-white"}`}></div>
            </div>
          </div>

          {canRemoveStudent && onRemoveStudent && (
            <button
              type="button"
              onClick={() => onRemoveStudent(student)}
              title={t("sessionDetails.removeStudent.buttonTitle")}
              className="p-2 rounded-xl text-rose-500 hover:bg-rose-500/10 hover:shadow-sm border border-transparent hover:border-rose-500/20 transition-all duration-200 shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {!measurementInfo.hasRecent ? (
        <div
          className={`bg-gradient-to-r ${isDark ? "from-red-900/50 to-red-800/50" : "from-red-50 to-red-100"} border ${isDark ? "border-red-600" : "border-red-200"} rounded-xl p-3 shadow-lg shadow-red-500/5`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle
                className={`w-4 h-4 ${isDark ? "text-red-400" : "text-red-500"} shrink-0`}
              />
              <span
                className={`text-xs ${isDark ? "text-red-300" : "text-red-700"}`}
              >
                <strong className="font-semibold">{t("sessionDetails.measurement.warning")}:</strong>{" "}
                {measurementInfo.message}
              </span>
            </div>
            {hasPermission("create:measurements") && (
              <button
                onClick={() => onAddMeasurement(student)}
                className="px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 font-semibold text-[10px] uppercase tracking-wider shadow-md shrink-0"
              >
                {t("sessionDetails.measurement.addButton")}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div
          className={`bg-gradient-to-r ${isDark ? "from-green-900/50 to-green-800/50" : "from-green-50 to-green-100"} border ${isDark ? "border-green-600" : "border-green-200"} rounded-xl p-3 shadow-lg shadow-green-500/5`}
        >
          <div className="flex items-center gap-2">
            <CheckCircle2
              className={`w-4 h-4 ${isDark ? "text-green-400" : "text-green-500"} shrink-0`}
            />
            <span
              className={`text-xs ${isDark ? "text-green-300" : "text-green-700"}`}
            >
              <strong className="font-semibold">{t("sessionDetails.measurement.label")}:</strong>{" "}
              {measurementInfo.message}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const SessionStudentSection = ({
  expanded,
  onToggle,
  studentsData,
  loadingStudents,
  onNavigateToStudent,
  onUpdateStatus,
  onAddMeasurement,
  onRemoveStudent,
}) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  // Per-student remove is only meaningful for group sessions (more than one
  // student row). For solo sessions we already have the page-level delete
  // button in SessionHeader, so we don't double up.
  const isGroupSession = (studentsData?.length || 0) > 1;
  const canRemoveStudent = isGroupSession && hasPermission("delete:sessions");

  const getStudentInitials = (name, surname) => {
    if (!name && !surname) return "ST";
    const firstInitial = name ? name.charAt(0).toUpperCase() : "";
    const lastInitial = surname ? surname.charAt(0).toUpperCase() : "";
    return firstInitial + lastInitial;
  };

  const getStatusConfig = (status) => {
    const s = (status || "scheduled").toLowerCase();
    switch (s) {
      case "completed":
        return {
          icon: CheckCircle,
          color: "text-emerald-500",
          bg: "bg-emerald-500/10",
          label: t("sessionDetails.status.completed", "Tamamlandı"),
        };
      case "cancelled":
        return {
          icon: XCircle,
          color: "text-rose-500",
          bg: "bg-rose-500/10",
          label: t("sessionDetails.status.cancelled", "İptal"),
        };
      case "no_show":
      case "no-show":
        return {
          icon: AlertCircle,
          color: "text-orange-500",
          bg: "bg-orange-500/10",
          label: t("sessionDetails.status.noShow", "Katılmadı"),
        };
      case "pending":
        return {
          icon: Clock,
          color: "text-yellow-500",
          bg: "bg-yellow-500/10",
          label: t("sessionDetails.status.pending"),
        };
      default:
        return {
          icon: Clock,
          color: "text-blue-500",
          bg: "bg-blue-500/10",
          label: t("sessionDetails.status.scheduled"),
        };
    }
  };

  const statusOptions = [
    {
      value: "scheduled",
      label: t("sessionDetails.status.scheduled", "Planlandı"),
      icon: Clock,
      colorClass: "text-blue-500",
    },
    {
      value: "completed",
      label: t("sessionDetails.status.completed", "Tamamlandı"),
      icon: CheckCircle,
      colorClass: "text-emerald-500",
    },
    {
      value: "cancelled",
      label: t("sessionDetails.status.cancelled", "İptal"),
      icon: XCircle,
      colorClass: "text-rose-500",
    },
    {
      value: "no-show",
      label: t("sessionDetails.status.noShow", "Katılmadı"),
      icon: AlertCircle,
      colorClass: "text-orange-500",
    },
  ];

  return (
    <div
      className={`${colors.cardBg} backdrop-blur-sm rounded-2xl ${colors.cardBorder} shadow-lg mb-6 ${expanded ? "relative z-30" : "overflow-hidden"}`}
    >
      <div
        className={`flex items-center justify-between p-6 cursor-pointer ${colors.tableRowHover} transition-colors ${expanded ? "rounded-t-2xl" : ""}`}
        onClick={onToggle}
      >
        <h2
          className={`text-lg font-bold ${colors.textPrimary} flex items-center`}
        >
          <User className={`w-5 h-5 mr-2 ${colors.textSecondary}`} />
          {t("sessionDetails.students", "Öğrenciler")} (
          {studentsData?.length || 0})
        </h2>
        {expanded ? (
          <ChevronUp className={`w-5 h-5 ${colors.textTertiary}`} />
        ) : (
          <ChevronDown className={`w-5 h-5 ${colors.textTertiary}`} />
        )}
      </div>

      {expanded && (
        <div
          className={`p-6 border-t ${colors.tableBorder} space-y-6 rounded-b-2xl`}
        >
          {loadingStudents ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="large" />
            </div>
          ) : studentsData && studentsData.length > 0 ? (
            studentsData.map((student) => (
              <StudentCard
                key={student.sessionId}
                student={student}
                colors={colors}
                isDark={isDark}
                t={t}
                user={user}
                onUpdateStatus={onUpdateStatus}
                onNavigateToStudent={onNavigateToStudent}
                onAddMeasurement={onAddMeasurement}
                onRemoveStudent={onRemoveStudent}
                canRemoveStudent={canRemoveStudent}
                hasPermission={hasPermission}
                getStudentInitials={getStudentInitials}
                getStatusConfig={getStatusConfig}
                statusOptions={statusOptions}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <div
                className={`w-16 h-16 ${colors.emptyStateBg} rounded-full flex items-center justify-center mb-4`}
              >
                <AlertCircle className={`w-8 h-8 ${colors.emptyStateIcon}`} />
              </div>
              <span className={colors.textMuted}>
                {t("sessionDetails.studentStats.noParticipants")}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
